'use client';

import Image from 'next/image';
import { HTMLAttributeAnchorTarget, MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { getAmplitudeItineraryPrice } from '../../../../../design-system/components/card/itinerary/ItineraryCard';
import { Ratio, RATIO } from '../../../../../design-system/components/ratio/Ratio';
import { Typography } from '../../../../../design-system/components/typography/Typography';
import { Carousel, useCarousel } from '../../../../../domains/Layout/Carousel/Carousel';
import {
    imageLoaderForId,
    imageLoaderForPicture,
} from '../../../../../domains/Layout/Image/URL/ImageURL';
import {
    PostBlockWithDestinationSliceType,
    PostBlockWithItinerarySliceType,
} from '../../../../../domains/Prismic/BrandPages/BrandPagesDocument';
import { useLocale } from '../../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../../shared/hooks/useUrlGenerator';
import { useCurrency } from '../../../../../shared/providers/currency/useCurrency';
import { roundedPriceWithCurrencySymbol } from '../../../../../shared/utilities/priceFormat/priceFormat';
import { AdditionalData, DestinationData, ItineraryData } from '../../PageView';
import { PostBlock } from '../Nested/PostBlock';

import style from './PostBlockWithDestinationOrItinerarySlice.module.css';

export type PostBlockWithDestinationOrItinerarySliceProps = (
    | PostBlockWithDestinationSliceType
    | PostBlockWithItinerarySliceType
) & {
    gridSliceIndex?: number;
    sliceIndex?: number;
};

export function PostBlockWithDestinationOrItinerarySlice({
    title,
    text,
    picture,
    ctaLabel,
    ctaLink,
    items,
    data,
    gridSliceIndex,
    sliceIndex,
    target,
}: PostBlockWithDestinationOrItinerarySliceProps & {
    data: AdditionalData;
}) {
    const carouselProps = useCarousel();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();
    const { currentCurrency } = useCurrency();

    return (
        <div className={style.wrapper}>
            <PostBlock
                title={title}
                text={text}
                picture={picture}
                ctaLabel={ctaLabel}
                ctaLink={ctaLink}
                gridSliceIndex={gridSliceIndex}
                sliceIndex={sliceIndex}
                target={target}
            />
            <div className={style.carouselWrapper}>
                <Carousel
                    {...carouselProps}
                    slidesSize="medium"
                    slidesVisibleRules={{
                        sm: 3,
                        md: 4,
                        lg: 3,
                        xl: 4,
                        xxl: 4,
                    }}
                    trackingInformation={{
                        navClick: {
                            block: 'carousel',
                            block_index: sliceIndex ? sliceIndex + 1 : 0,
                            block_title: title ?? undefined,
                        },
                    }}
                >
                    {items
                        .map((item, index) => {
                            if (
                                'destinationId' in item &&
                                data.destinations &&
                                item.destinationId
                            ) {
                                const destination = data.destinations?.[item.destinationId];
                                if (destination) {
                                    return (
                                        <DestinationCarouselCard
                                            key={destination.id}
                                            destination={destination}
                                            href={generatePath('destination', {
                                                destinationSlug: destination.slug,
                                            })}
                                            onClick={() => {
                                                trackEvent((ampli, defaultProperties) => {
                                                    ampli.destinationClicked({
                                                        ...defaultProperties,
                                                        destination_id: destination.id,
                                                        destination_slug: destination.slug,
                                                        post_title: title ?? '',
                                                        block: 'carousel',
                                                        card_number: index + 1,
                                                        tool_counter: 0,
                                                    });
                                                });
                                            }}
                                        />
                                    );
                                }
                            }
                            if ('itineraryId' in item && data.itineraries && item.itineraryId) {
                                const itinerary = data.itineraries?.[item.itineraryId];
                                if (itinerary) {
                                    return (
                                        <ItineraryCarouselCard
                                            key={itinerary.id}
                                            itinerary={itinerary}
                                            href={generatePath('itinerary', {
                                                destinationSlug: itinerary.destination.slug,
                                                itineraryId: itinerary.id,
                                                itinerarySlug: itinerary.slug,
                                            })}
                                            onClick={() => {
                                                trackEvent((ampli, defaultProperties) => {
                                                    ampli.itineraryClicked({
                                                        ...defaultProperties,
                                                        itinerary_id: itinerary.id,
                                                        itinerary_slug: itinerary.slug,
                                                        post_title: title ?? '',
                                                        block: 'carousel',
                                                        card_number: index + 1,
                                                        agency_better_trip_index:
                                                            itinerary.agency.betterTripIndex,
                                                        agency_id: itinerary.agency.id,
                                                        agency_slug: itinerary.agency.slug,
                                                        duration: itinerary.duration.toString(),
                                                        price: getAmplitudeItineraryPrice(
                                                            itinerary.startingPrice,
                                                            currentCurrency,
                                                        ),
                                                        tags: [],
                                                        row_number: 0,
                                                        destination_id: itinerary.destination.id,
                                                        destination_slug:
                                                            itinerary.destination.slug,
                                                        tool_counter: 0,
                                                    });
                                                });
                                            }}
                                        />
                                    );
                                }
                            }
                        })
                        .filter(Boolean)}
                </Carousel>
            </div>
        </div>
    );
}

type DestinationCarouselCardProps = {
    destination: DestinationData;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    href?: string;
    target?: HTMLAttributeAnchorTarget;
};

function DestinationCarouselCard({
    destination,
    onClick,
    href,
    target,
}: DestinationCarouselCardProps) {
    const ASPECT_RATIO = RATIO['1/1'];
    const { name, coverPicture } = destination;
    const { legend, id } = coverPicture;

    return (
        <div className={style.card}>
            {coverPicture ? (
                <div className={style.imageWrapper}>
                    <Ratio ratio={ASPECT_RATIO}>
                        <Image
                            className={style.image}
                            alt={legend}
                            src={id}
                            fill
                            loading="lazy"
                            loader={imageLoaderForId({
                                aspectRatio: ASPECT_RATIO,
                            })}
                        />
                    </Ratio>
                </div>
            ) : null}
            <a onClick={onClick} className={style.link} href={href} target={target}>
                <Typography as="p" scale="primary-body-bold">
                    {name}
                </Typography>
            </a>
        </div>
    );
}

type ItineraryCarouselCardProps = {
    itinerary: ItineraryData;
    onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
    href?: string;
    target?: HTMLAttributeAnchorTarget;
};

function ItineraryCarouselCard({ itinerary, onClick, href, target }: ItineraryCarouselCardProps) {
    const { currentCurrency } = useCurrency();
    const { formatMessage } = useIntl();
    const { locale } = useLocale();
    const ASPECT_RATIO = RATIO['16/10'];
    const { destination, picture, title, startingPrice, duration } = itinerary;
    const { legend, id } = picture;

    return (
        <div className={style.card}>
            {picture ? (
                <div className={style.imageWrapper}>
                    <Ratio ratio={ASPECT_RATIO}>
                        <Image
                            className={style.image}
                            alt={legend}
                            fill
                            loading="lazy"
                            src={id.toString()}
                            loader={imageLoaderForPicture(picture, {
                                aspectRatio: ASPECT_RATIO,
                            })}
                        />
                    </Ratio>
                </div>
            ) : null}
            <Typography as="p" scale="primary-body-bold">
                {destination.name}
            </Typography>
            <Typography as="p" scale="primary-xxs-regular">
                {formatMessage(
                    { id: 'global.component.itineraryCard.durationWithPrice' },
                    {
                        duration: duration,
                        price: roundedPriceWithCurrencySymbol({
                            currency: currentCurrency,
                            locale,
                            price: startingPrice,
                        }),
                    },
                )}
            </Typography>
            <a onClick={onClick} className={style.link} href={href} target={target}>
                <Typography as="p" scale="primary-xs-bold" className={style.itineraryTitle}>
                    {title}
                </Typography>
            </a>
        </div>
    );
}
